/**
 * E45 · the portal's side of the Sleep diary — the field lists the data provider asks for, the shapes it gets back, and the pure helpers
 * the hub's Sleep diary tab uses. The rules themselves (the order of the times, how far back a night may be entered, the window floors,
 * who may act for the client) are the deciders' and the resolver's; the hub only shapes what it sends. Numbers, not verdicts.
 */
export type DiaryNightRow = {
  night: string;
  nightKind: 'slept' | 'noSleep' | 'skipped';
  source: 'device' | 'manual' | 'paper';
  performer: string | null;
  performerName: string | null;
  bedTime: string | null;
  lightsOut: string | null;
  finalWake: string | null;
  outOfBed: string | null;
  timeInBedMinutes: number | null;
  totalSleepMinutes: number | null;
  efficiency: number | null;
  sleepOnsetMinutes: number | null;
  awakenings: number | null;
  wakeMinutes: number | null;
  quality: number | null;
  note: string | null;
  watchAsleepMinutes: number | null;
};

export type WeekAveragesRow = {
  nightsFilled: number;
  timeInBedMinutes: number | null;
  totalSleepMinutes: number | null;
  efficiency: number | null;
  sleepOnsetMinutes: number | null;
  wakeMinutes: number | null;
  quality: number | null;
};

export type WindowRow = {
  windowId: string;
  performer: string;
  performerName: string | null;
  bedtime: string;
  riseTime: string;
  effectiveFrom: string;
  protocol: 'cbti' | 'bbti';
  note: string | null;
  lengthMinutes: number | null;
  setAt: string;
  endedAt: string | null;
  basis: {
    nights: number;
    meanTimeInBed: string;
    meanTotalSleep: string;
    meanEfficiency: number;
  } | null;
};

export type UsualRuleRow = {
  protocol: 'cbti' | 'bbti';
  action: 'moreInBed' | 'keep' | 'lessInBed';
  minutes: number;
  atFloor: boolean;
  nights: number;
  meanEfficiency: number | null;
  meanFellAsleep: number | null;
  meanAwakeInNight: number | null;
};

export type SleepWeekRow = {
  subject: string;
  week: string;
  watchShared: boolean;
  upcoming: boolean;
  nights: DiaryNightRow[];
  averages: WeekAveragesRow;
  weeks: Array<{ week: string; averages: WeekAveragesRow }>;
  window: WindowRow | null;
  windows: WindowRow[];
  /** The averages of the last seven nights (yesterday back), whatever week is shown — the basis of a window and of the rule. */
  lastSevenNights: WeekAveragesRow | null;
  usualRule: UsualRuleRow | null;
};

const AVERAGES = [
  'nightsFilled',
  'timeInBedMinutes',
  'totalSleepMinutes',
  'efficiency',
  'sleepOnsetMinutes',
  'wakeMinutes',
  'quality',
];

const WINDOW_FIELDS = [
  'windowId',
  'performer',
  'performerName',
  'bedtime',
  'riseTime',
  'effectiveFrom',
  'protocol',
  'note',
  'lengthMinutes',
  'setAt',
  'endedAt',
  { basis: ['nights', 'meanTimeInBed', 'meanTotalSleep', 'meanEfficiency'] },
];

export const SLEEP_WEEK_FIELDS = [
  'subject',
  'week',
  'watchShared',
  'upcoming',
  {
    nights: [
      'night',
      'nightKind',
      'source',
      'performer',
      'performerName',
      'bedTime',
      'lightsOut',
      'finalWake',
      'outOfBed',
      'timeInBedMinutes',
      'totalSleepMinutes',
      'efficiency',
      'sleepOnsetMinutes',
      'awakenings',
      'wakeMinutes',
      'quality',
      'note',
      'watchAsleepMinutes',
    ],
  },
  { averages: AVERAGES },
  { weeks: ['week', { averages: AVERAGES }] },
  { window: WINDOW_FIELDS },
  { windows: WINDOW_FIELDS },
  { lastSevenNights: AVERAGES },
  {
    usualRule: [
      'protocol',
      'action',
      'minutes',
      'atFloor',
      'nights',
      'meanEfficiency',
      'meanFellAsleep',
      'meanAwakeInNight',
    ],
  },
];

export const COMMAND_FIELDS = ['recorded', 'id'];

/** `YYYY-MM-DD` of a Date in the browser's zone. */
export const dayOf = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const dateOf = (day: string): Date => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const addDays = (day: string, n: number): string => {
  const d = dateOf(day);
  d.setDate(d.getDate() + n);
  return dayOf(d);
};

export const mondayOf = (day: string): string => {
  const d = dateOf(day);
  return addDays(day, -((d.getDay() + 6) % 7));
};

/** 475 → "7 h 55" in the portal's units (the strings come from i18n; this is the pure split). */
export const hoursMinutes = (
  minutes: number,
): { hours: number; minutes: string } => ({
  hours: Math.floor(minutes / 60),
  minutes: String(minutes % 60).padStart(2, '0'),
});

/** "23:10" | "7:05" | "2310" → "HH:MM", or null. */
export const normalClock = (value: string): string | null => {
  const v = value.trim();
  const hit = /^(\d{1,2})[:.](\d{2})$/.exec(v) ?? /^(\d{2})(\d{2})$/.exec(v);
  if (!hit) return null;
  const h = Number(hit[1]);
  const m = Number(hit[2]);
  return h <= 23 && m <= 59
    ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    : null;
};

const instant = (night: string, clock: string, after: Date | null): Date => {
  const [h, m] = clock.split(':').map(Number);
  const base = dateOf(night);
  if (!after) {
    // the first time of the night: from noon it is that evening, a small hour belongs to the next day
    return new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate() + (h < 12 ? 1 : 0),
      h,
      m,
    );
  }
  const same = new Date(
    after.getFullYear(),
    after.getMonth(),
    after.getDate(),
    h,
    m,
  );
  return same.getTime() < after.getTime()
    ? new Date(same.getFullYear(), same.getMonth(), same.getDate() + 1, h, m)
    : same;
};

export type NightDraft = {
  night: string;
  kind: 'slept' | 'noSleep' | 'skipped';
  bedTime: string;
  lightsOut: string;
  sleepOnsetMinutes: string;
  awakenings: string;
  wakeMinutes: string;
  finalWake: string;
  outOfBed: string;
  quality: number | null;
  note: string;
};

export const emptyNightDraft = (night = ''): NightDraft => ({
  night,
  kind: 'slept',
  bedTime: '',
  lightsOut: '',
  sleepOnsetMinutes: '',
  awakenings: '',
  wakeMinutes: '',
  finalWake: '',
  outOfBed: '',
  quality: null,
  note: '',
});

const whole = (v: string): number | null =>
  /^\d{1,4}$/.test(v.trim()) ? Number(v.trim()) : null;

/** A night may be entered for up to this many days back (the decider's SLEEP_NIGHT_TOO_OLD for a therapist). */
export const NIGHT_DAYS_BACK = 14;

/** Is this day a night the picker offers? Not today or later (the night must have ended), not more than fourteen days back. */
export const nightSelectable = (day: string, today: string): boolean =>
  day < today && day >= addDays(today, -NIGHT_DAYS_BACK);

export type NightProblem =
  | 'nightRequired'
  | 'timesRequired'
  | 'noSleepRequired'
  | 'minutesRequired'
  | 'qualityRequired';

/**
 * What stops "Enter a night" from sending, as the key under `sleepDiary.enter.*` — the FIRST problem, so the sentence says what to fix
 * (a full night needs every time as a 24-hour time, then whole minutes, then the quality; a night with no sleep, into bed and out of bed
 * and the quality; a skipped night only its date). Null: ready.
 */
export const nightProblem = (d: NightDraft): NightProblem | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.night)) return 'nightRequired';
  if (d.kind === 'skipped') return null;
  if (d.kind === 'noSleep') {
    if (!normalClock(d.bedTime) || !normalClock(d.outOfBed))
      return 'noSleepRequired';
    return d.quality === null ? 'qualityRequired' : null;
  }
  if (
    !normalClock(d.bedTime) ||
    !normalClock(d.lightsOut) ||
    !normalClock(d.finalWake) ||
    !normalClock(d.outOfBed)
  )
    return 'timesRequired';
  const blank = (v: string) => v.trim() === '';
  if (
    whole(d.sleepOnsetMinutes) === null ||
    (!blank(d.awakenings) && whole(d.awakenings) === null) ||
    (!blank(d.wakeMinutes) && whole(d.wakeMinutes) === null)
  )
    return 'minutesRequired';
  return d.quality === null ? 'qualityRequired' : null;
};

/**
 * The "Enter a night" input — null while the draft is not ready (a full night needs every time; a night with no sleep needs into bed and
 * out of bed; a skipped night only its date). The times become instants in the browser's zone, put in the order the night happened.
 */
export const nightInput = (d: NightDraft, subject: string, today: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d.night)) return null;
  const base = {
    subject,
    night: d.night,
    today,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    nightKind: d.kind,
    source: 'manual',
  };
  if (d.kind === 'skipped') return { ...base, note: d.note.trim() || null };
  const bed = normalClock(d.bedTime);
  const out = normalClock(d.outOfBed);
  if (!bed || !out || d.quality === null) return null;
  let at: Date | null = instant(d.night, bed, null);
  const bedTime = at;
  if (d.kind === 'noSleep') {
    const outOfBed = instant(d.night, out, at);
    return {
      ...base,
      bedTime: bedTime.toISOString(),
      outOfBed: outOfBed.toISOString(),
      quality: d.quality,
      note: d.note.trim() || null,
    };
  }
  const lights = normalClock(d.lightsOut);
  const wake = normalClock(d.finalWake);
  const onset = whole(d.sleepOnsetMinutes);
  if (!lights || !wake || onset === null) return null;
  const lightsOut = (at = instant(d.night, lights, at));
  const finalWake = (at = instant(d.night, wake, at));
  const outOfBed = instant(d.night, out, at);
  return {
    ...base,
    bedTime: bedTime.toISOString(),
    lightsOut: lightsOut.toISOString(),
    sleepOnsetMinutes: onset,
    awakenings: whole(d.awakenings) ?? 0,
    wakeMinutes: whole(d.wakeMinutes) ?? 0,
    finalWake: finalWake.toISOString(),
    outOfBed: outOfBed.toISOString(),
    quality: d.quality,
    note: d.note.trim() || null,
  };
};

export type WindowDraft = {
  bedtime: string;
  riseTime: string;
  effectiveFrom: string;
  protocol: 'cbti' | 'bbti';
  note: string;
};

export const emptyWindowDraft = (
  protocol: 'cbti' | 'bbti' = 'cbti',
  effectiveFrom = '',
): WindowDraft => ({
  bedtime: '',
  riseTime: '',
  effectiveFrom,
  protocol,
  note: '',
});

/**
 * The nights the window is set from, as the board records it (`basis`): the LAST SEVEN NIGHTS (yesterday back) and their means — whatever
 * week is on screen (Q-sleep-1 A, 2026-10-19). The server averages them (`lastSevenNights`, the same averaging as a week's).
 */
export const windowBasis = (
  seven: Pick<SleepWeekRow, 'lastSevenNights'>,
  hm: (minutes: number) => string,
): {
  nights: number;
  meanTimeInBed: string;
  meanTotalSleep: string;
  meanEfficiency: number;
} | null => {
  const a = seven.lastSevenNights;
  return a &&
    a.nightsFilled > 0 &&
    a.timeInBedMinutes !== null &&
    a.totalSleepMinutes !== null &&
    a.efficiency !== null
    ? {
        nights: a.nightsFilled,
        meanTimeInBed: hm(a.timeInBedMinutes),
        meanTotalSleep: hm(a.totalSleepMinutes),
        meanEfficiency: a.efficiency,
      }
    : null;
};

/** What stops "Set a window" from sending, as the key under `sleep.hub.window.*`; null: ready. */
export const windowProblem = (
  d: WindowDraft,
): 'timesRequired' | 'dateRequired' | null =>
  !normalClock(d.bedtime) || !normalClock(d.riseTime)
    ? 'timesRequired'
    : !/^\d{4}-\d{2}-\d{2}$/.test(d.effectiveFrom)
    ? 'dateRequired'
    : null;

export const windowInput = (
  d: WindowDraft,
  subject: string,
  basis: ReturnType<typeof windowBasis>,
) => {
  const bedtime = normalClock(d.bedtime);
  const riseTime = normalClock(d.riseTime);
  if (!bedtime || !riseTime || !/^\d{4}-\d{2}-\d{2}$/.test(d.effectiveFrom))
    return null;
  return {
    subject,
    bedtime,
    riseTime,
    effectiveFrom: d.effectiveFrom,
    protocol: d.protocol,
    basis,
    note: d.note.trim() || null,
  };
};

/**
 * The daily "keep the diary" step: every day for `days` days from tomorrow at 09:00 in the CLIENT's zone (the E34 repeat rule); each
 * occurrence is completed by that night's confirmation (the evidence matcher's sleep-diary row).
 */
export const diarySchedule = (
  from: Date,
  days: number,
  tzid: string,
): { rrule: string; dtstart: string; tzid: string; exdates: string[] } => {
  const next = new Date(
    from.getFullYear(),
    from.getMonth(),
    from.getDate() + 1,
  );
  return {
    rrule: `FREQ=DAILY;COUNT=${days}`,
    dtstart: `${dayOf(next)}T09:00:00`,
    tzid,
    exdates: [],
  };
};

/** How many of the last seven nights the diary holds filled — the hub warns (never refuses) under five. */
export const fewNights = (
  seven: Pick<SleepWeekRow, 'lastSevenNights'>,
): boolean => (seven.lastSevenNights?.nightsFilled ?? 0) < 5;
